import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { EIntegrationTypes, EPackageTiers } from '@feathr/blackbox';
import { Nav, NavItem, NavMenu } from '@feathr/components';
import { TrackBlocked } from '@feathr/extender/components';
import {
  useGoogleAdsPermissions,
  useLicense,
  useMetaIntegrationStatus,
  useMinPackageTooltip,
} from '@feathr/extender/hooks';
import { useAccount, useFlags, useLocalUrl, useRole } from '@feathr/extender/state';

function SettingsNav(): JSX.Element {
  const localUrl = useLocalUrl();
  const account = useAccount();
  const { t } = useTranslation();
  const {
    hasBilling,
    hasConversions,
    hasDomains,
    hasIntegrations,
    isSudoer,
    isAdmin,
    hasPermissionsLicenseOrFlag,
  } = useRole();
  const { hasImis: hasImisFlag, hasBlackbaudRaisersEdge: hasBlackbaudRaisersEdgeFlag } = useFlags();
  const {
    proResourcesPageVisible,
    iMISDirectIntegration,
    blackbaudDirectIntegration,
    hasApiKeys,
    googleAdsSmart: hasGoogleAdsSmart,
    directIntegrations,
    zapier,
    facebook: hasFacebook,
  } = useLicense([
    'proResourcesPageVisible',
    'iMISDirectIntegration',
    'blackbaudDirectIntegration',
    'hasApiKeys',
    'googleAdsSmart',
    'directIntegrations',
    'zapier',
    'facebook',
  ]);

  const showImis = iMISDirectIntegration || hasImisFlag;
  const showBlackbaudRaisersEdge = blackbaudDirectIntegration || hasBlackbaudRaisersEdgeFlag;
  const googleAdsPermissions = useGoogleAdsPermissions({ requireConnectedCustomer: false });
  const showGoogleAds = googleAdsPermissions || hasGoogleAdsSmart;
  const metaIntegrationStatus = useMetaIntegrationStatus();

  const directIntegrationsTooltip = useMinPackageTooltip('directIntegrations', 'data');
  const zapierTooltip = useMinPackageTooltip('zapier', 'data');
  const integrationsMenuEnabled =
    showImis || showBlackbaudRaisersEdge || showGoogleAds || directIntegrations > 0 || zapier > 0;

  const tooltipsByFeature = {
    directIntegrations: directIntegrationsTooltip,
    zapier: zapierTooltip,
  };

  let integrationsTooltip: string | undefined;

  if (integrationsMenuEnabled) {
    // When integrations menu is enabled, prefer directIntegrations tooltip
    if (tooltipsByFeature.directIntegrations) {
      integrationsTooltip = tooltipsByFeature.directIntegrations;
    } else if (tooltipsByFeature.zapier) {
      integrationsTooltip = tooltipsByFeature.zapier;
    }
  } else {
    // When integrations menu is disabled, prefer zapier tooltip
    if (tooltipsByFeature.zapier) {
      integrationsTooltip = tooltipsByFeature.zapier;
    } else if (tooltipsByFeature.directIntegrations) {
      integrationsTooltip = tooltipsByFeature.directIntegrations;
    }
  }

  const isImisConflicting = account.conflictingIntegrations.includes(EIntegrationTypes.Imis);
  const isBlackbaudConflicting = account.conflictingIntegrations.includes(
    EIntegrationTypes.BlackbaudRaisersEdge,
  );

  const billingNav = (
    <Nav indented={true} label={'Billing settings navigation'} type={'vertical'}>
      <NavItem to={localUrl('/settings/billing/configurations')}>{t('Configurations')}</NavItem>
      <NavItem to={localUrl('/settings/billing/license')}>{t('License')}</NavItem>
      <NavItem to={localUrl('/settings/billing/invoices')}>{t('Invoices')}</NavItem>
    </Nav>
  );

  const userNav = (
    <Nav
      indented={true}
      label={'User settings navigation'}
      name={'nav_user_menu'}
      type={'vertical'}
    >
      <NavItem to={localUrl('/settings/user/notifications')}>{t('Notifications')}</NavItem>
      <NavItem to={localUrl('/settings/user/profile')}>{t('Profile')}</NavItem>
      <NavItem to={localUrl('/settings/user/reset-password')}>{t('Reset Password')}</NavItem>
      {(account.isInternal || isSudoer) && (
        <NavItem to={localUrl('/settings/user/internal')}>{t('Internal')}</NavItem>
      )}
    </Nav>
  );

  const accountNav = (
    <Nav
      indented={true}
      label={'Account settings navigation'}
      name={'nav_account_menu'}
      type={'vertical'}
    >
      {(account.get('is_agency') || isSudoer) && (
        <NavItem to={localUrl('/settings/account/advertisers')}>{t('Advertisers')}</NavItem>
      )}
      {hasConversions && (
        <NavItem to={localUrl('/settings/account/conversions')}>{t('Conversions')}</NavItem>
      )}
      {hasDomains && (
        <>
          <NavItem to={localUrl('/settings/account/domains')}>{t('Domains')}</NavItem>
          <NavItem to={localUrl('/settings/account/allow-list')}>{t('Domain Allow List')}</NavItem>
        </>
      )}
      <NavItem to={localUrl('/settings/account/emails')}>{t('Email Addresses')}</NavItem>
      <NavItem to={localUrl('/settings/account/templates')}>{t('Templates')}</NavItem>
      <NavItem to={localUrl('/settings/account/ip-filtering')}>{t('IP Filtering')}</NavItem>
      {(account.isInternal || isSudoer) && (
        <NavItem to={localUrl('/settings/account/flags')}>{t('Flags')}</NavItem>
      )}
      <NavItem to={localUrl('/settings/account/fonts')}>{t('Fonts')}</NavItem>
      {(isAdmin || isSudoer) && (
        <NavItem to={localUrl('/settings/account/general')}>{t('General')}</NavItem>
      )}
      {isSudoer && <NavItem to={localUrl('/settings/account/profile')}>{t('Profile')}</NavItem>}
      <NavItem to={localUrl('/settings/account/subscriptions')}>{t('Subscriptions')}</NavItem>
      {(isAdmin || isSudoer) && (
        <NavItem to={localUrl('/settings/account/users')}>
          {hasPermissionsLicenseOrFlag ? t('Users & Roles') : t('Users')}
        </NavItem>
      )}
    </Nav>
  );

  const integrationsNav = (
    <Nav
      indented={true}
      label={'Integrations settings navigation'}
      name={'nav_integrations_menu'}
      type={'vertical'}
    >
      {hasApiKeys && (
        <NavItem to={localUrl('settings/integrations/api-keys')}>{t('API Keys')}</NavItem>
      )}
      {showGoogleAds && (
        <NavItem to={localUrl('/settings/integrations/google-ads')}>{t('Google Ads')}</NavItem>
      )}
      {showImis && !isImisConflicting && (
        <NavItem to={localUrl('/settings/integrations/imis')}>{t('iMIS')}</NavItem>
      )}
      {!metaIntegrationStatus.isDisabled && hasFacebook && (
        <NavItem to={localUrl('/settings/integrations/meta')}>{t('Meta')}</NavItem>
      )}
      {showBlackbaudRaisersEdge && !isBlackbaudConflicting && (
        <NavItem to={localUrl('/settings/integrations/raisers-edge')}>
          {t("Raiser's Edge NXT")}
        </NavItem>
      )}
    </Nav>
  );

  return (
    <Nav indented={true} label={'Settings navigation'} name={'nav_settings_menu'} type={'vertical'}>
      <NavMenu menu={userNav} name={'settings_user'} to={localUrl('/settings/user')}>
        {t('User')}
      </NavMenu>
      <NavMenu menu={accountNav} name={'settings_account'} to={localUrl('/settings/account')}>
        {t('Account')}
      </NavMenu>
      {hasIntegrations && (
        <TrackBlocked
          featureName={'integrations'}
          locked={!integrationsMenuEnabled}
          requiredPackage={directIntegrations > 0 ? undefined : EPackageTiers.Essential}
        >
          <NavMenu
            menu={integrationsNav}
            name={'settings_integrations'}
            to={localUrl('/settings/integrations')}
            tooltip={integrationsTooltip}
          >
            {t('Integrations')}
          </NavMenu>
        </TrackBlocked>
      )}
      {hasBilling && (
        <NavMenu menu={billingNav} name={'settings_billing'} to={localUrl('/settings/billing')}>
          {t('Billing')}
        </NavMenu>
      )}
      {proResourcesPageVisible && (
        <NavItem to={localUrl('/settings/pro-resources')}>{t('Resources')}</NavItem>
      )}
    </Nav>
  );
}

export default observer(SettingsNav);
